(function () {
  'use strict';

  var DRAWER_SELECTOR = '[data-cart-drawer]';
  var OPEN_CLASS = 'is-open';
  var EMPTY_CLASS = 'is-empty';
  var SCROLL_LOCK_CLASS = 'oddit-cart-drawer-lock';
  // Fill length = track up to the truck circle (circle starts 254px into the 300px bar, track starts at 5px)
  var PROGRESS_TRACK_PX = 249;

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var abortController = null;
  var cartState = null;
  var activeCurrency = null;
  var lastFocused = null;

  function getDrawer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  /* -----------------------------------------------------------------
   * Money formatting — currency is resolved dynamically, never
   * hardcoded, so markets / multi-currency stores render correctly.
   * --------------------------------------------------------------- */
  function getActiveCurrency() {
    if (activeCurrency) return activeCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  }

  function formatMoney(cents) {
    var currency = getActiveCurrency();
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] || ch
      );
    });
  }

  function sizedImageUrl(url, width, height) {
    if (!url) return '';
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var params = 'width=' + width;
    if (height) params += '&height=' + height + '&crop=center';
    return url + sep + params;
  }

  function dispatchCartUpdated(cart) {
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    } catch (err) {
      /* noop */
    }
  }

  /* -----------------------------------------------------------------
   * Reveal activation — the fixed overlay never scrolls, so the global
   * IntersectionObserver-driven oddit-reveal system never fires inside
   * it. Manually mark reveal children visible right after opening.
   * --------------------------------------------------------------- */
  function activateReveals(root) {
    var reveals = root.querySelectorAll('.oddit-reveal');
    for (var i = 0; i < reveals.length; i++) {
      reveals[i].classList.add('oddit-is-visible');
    }
  }

  /* -----------------------------------------------------------------
   * Focus trap
   * --------------------------------------------------------------- */
  function getFocusable(root) {
    var panel = root.querySelector('.oddit-cart-drawer__panel');
    if (!panel) return [];
    return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE_SELECTOR), function (el) {
      return !el.hasAttribute('disabled') && el.offsetParent !== null && el.getAttribute('aria-hidden') !== 'true';
    });
  }

  function trapFocus(e, root) {
    var focusable = getFocusable(root);
    if (!focusable.length) {
      e.preventDefault();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* -----------------------------------------------------------------
   * Open / close / toggle
   * --------------------------------------------------------------- */
  function openDrawer() {
    var root = getDrawer();
    if (!root || root.classList.contains(OPEN_CLASS)) return;
    lastFocused = document.activeElement;
    if (root.style.display === 'none') {
      root.style.display = '';
      void root.offsetWidth;
    }
    root.classList.add(OPEN_CLASS);
    document.body.classList.add(SCROLL_LOCK_CLASS);
    activateReveals(root);
    refreshCart().catch(function () {
      /* keep the drawer usable even if the refresh fails */
    });
    window.requestAnimationFrame(function () {
      var closeBtn = root.querySelector('[data-cart-drawer-close]');
      var target = (closeBtn && closeBtn) || getFocusable(root)[0] || root.querySelector('.oddit-cart-drawer__panel');
      if (target && typeof target.focus === 'function') target.focus();
    });
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }

  function closeDrawer() {
    var root = getDrawer();
    if (!root || !root.classList.contains(OPEN_CLASS)) return;
    root.classList.remove(OPEN_CLASS);
    document.body.classList.remove(SCROLL_LOCK_CLASS);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try {
        lastFocused.focus();
      } catch (err) {
        /* noop */
      }
    }
    lastFocused = null;
    document.dispatchEvent(new CustomEvent('cart-drawer:close'));
  }

  function toggleDrawer() {
    var root = getDrawer();
    if (root && root.classList.contains(OPEN_CLASS)) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }

  /* -----------------------------------------------------------------
   * Shopify Cart AJAX API
   * --------------------------------------------------------------- */
  function jsonFetch(url, options) {
    var opts = options || {};
    var headers = Object.assign(
      { 'Content-Type': 'application/json', Accept: 'application/json' },
      opts.headers || {}
    );
    return fetch(url, {
      method: opts.method || 'GET',
      headers: headers,
      credentials: 'same-origin',
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!res.ok) {
            var err = new Error((data && (data.description || data.message)) || 'Cart request failed');
            err.status = res.status;
            err.data = data;
            throw err;
          }
          return data;
        });
    });
  }

  function addToCart(variantId, quantity, properties) {
    var body = { id: Number(variantId), quantity: quantity != null ? Number(quantity) : 1 };
    if (properties && Object.keys(properties).length) body.properties = properties;
    return jsonFetch('/cart/add.js', { method: 'POST', body: body })
      .then(function () {
        return getCart();
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function addMultipleToCart(items) {
    var normalized = (items || []).map(function (item) {
      var entry = { id: Number(item.id), quantity: item.quantity != null ? Number(item.quantity) : 1 };
      if (item.properties && Object.keys(item.properties).length) entry.properties = item.properties;
      return entry;
    });
    return jsonFetch('/cart/add.js', { method: 'POST', body: { items: normalized } })
      .then(function () {
        return getCart();
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return jsonFetch('/cart/change.js', {
      method: 'POST',
      body: { id: lineItemKey, quantity: Number(quantity) }
    }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
    return jsonFetch('/cart/change.js', {
      method: 'POST',
      body: { id: lineItemKey, selling_plan: Number(sellingPlanId) }
    }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function getCart() {
    return jsonFetch('/cart.js', { method: 'GET' });
  }

  function refreshCart() {
    return getCart().then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  /* -----------------------------------------------------------------
   * renderCart(cart) — the single DOM orchestrator. Every cart mutation
   * routes through here. [data-cart-additional-checkout] is never
   * touched so Shopify's shopify-accelerated-checkout-cart web
   * component survives every re-render.
   * --------------------------------------------------------------- */
  function buildLineItemHTML(item) {
    var key = item.key;
    var title = escapeHtml(item.product_title || item.title || '');
    var variantTitle = item.variant_title;
    var showVariant = variantTitle && variantTitle !== 'Default Title';
    var price = formatMoney(item.final_line_price);
    var imageUrl = item.image ? sizedImageUrl(item.image, 160, 160) : '';

    return (
      '<li class="oddit-cart-drawer__item" data-line-item-key="' + key + '">' +
      '<div class="oddit-cart-drawer__item-image">' +
      (imageUrl
        ? '<img class="oddit-cart-drawer__item-img" src="' +
          imageUrl +
          '" width="160" height="160" alt="' +
          title +
          '" loading="lazy">'
        : '') +
      '</div>' +
      '<div class="oddit-cart-drawer__item-details">' +
      '<div class="oddit-cart-drawer__item-top">' +
      '<div class="oddit-cart-drawer__item-title-row">' +
      '<h3 class="oddit-cart-drawer__item-title">' +
      title +
      '</h3>' +
      '<button type="button" class="oddit-cart-drawer__item-remove" data-cart-remove-item data-line-item-key="' +
      key +
      '" aria-label="Remove ' +
      title +
      '">' +
      '<svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M7.5 3.9375V4.125H10.5V3.9375C10.5 3.53968 10.342 3.15814 10.0607 2.87684C9.77936 2.59554 9.39782 2.4375 9 2.4375C8.60218 2.4375 8.22064 2.59554 7.93934 2.87684C7.65804 3.15814 7.5 3.53968 7.5 3.9375ZM6.5625 4.125V3.9375C6.5625 3.29103 6.81931 2.67105 7.27643 2.21393C7.73355 1.75681 8.35353 1.5 9 1.5C9.64647 1.5 10.2665 1.75681 10.7236 2.21393C11.1807 2.67105 11.4375 3.29103 11.4375 3.9375V4.125H15.6562C15.7806 4.125 15.8998 4.17439 15.9877 4.26229C16.0756 4.3502 16.125 4.46943 16.125 4.59375C16.125 4.71807 16.0756 4.8373 15.9877 4.92521C15.8998 5.01311 15.7806 5.0625 15.6562 5.0625H14.5624L13.8124 14.0085C13.7554 14.6879 13.4453 15.321 12.9434 15.7825C12.4416 16.2439 11.7847 16.5 11.103 16.5H6.897C6.21532 16.4999 5.55854 16.2438 5.0568 15.7823C4.55506 15.3209 4.24498 14.6878 4.188 14.0085L3.438 5.0625H2.34375C2.21943 5.0625 2.1002 5.01311 2.01229 4.92521C1.92439 4.8373 1.875 4.71807 1.875 4.59375C1.875 4.46943 1.92439 4.3502 2.01229 4.26229C2.1002 4.17439 2.21943 4.125 2.34375 4.125H6.5625ZM5.12213 13.9301C5.15945 14.3752 5.36261 14.79 5.69134 15.0923C6.02007 15.3947 6.45038 15.5625 6.897 15.5625H11.103C11.5497 15.5626 11.9801 15.3948 12.3089 15.0924C12.6377 14.7901 12.8409 14.3752 12.8783 13.9301L13.6215 5.0625H4.3785L5.12213 13.9301ZM8.0625 7.59375C8.0625 7.53219 8.05038 7.47124 8.02682 7.41437C8.00326 7.3575 7.96873 7.30582 7.92521 7.26229C7.88168 7.21877 7.83 7.18424 7.77313 7.16068C7.71626 7.13712 7.65531 7.125 7.59375 7.125C7.53219 7.125 7.47124 7.13712 7.41437 7.16068C7.3575 7.18424 7.30582 7.21877 7.26229 7.26229C7.21877 7.30582 7.18424 7.3575 7.16068 7.41437C7.13712 7.47124 7.125 7.53219 7.125 7.59375V13.0312C7.125 13.0928 7.13712 13.1538 7.16068 13.2106C7.18424 13.2675 7.21877 13.3192 7.26229 13.3627C7.30582 13.4062 7.3575 13.4408 7.41437 13.4643C7.47124 13.4879 7.53219 13.5 7.59375 13.5C7.65531 13.5 7.71626 13.4879 7.77313 13.4643C7.83 13.4408 7.88168 13.4062 7.92521 13.3627C7.96873 13.3192 8.00326 13.2675 8.02682 13.2106C8.05038 13.1538 8.0625 13.0928 8.0625 13.0312V7.59375ZM10.4062 7.125C10.665 7.125 10.875 7.335 10.875 7.59375V13.0312C10.875 13.1556 10.8256 13.2748 10.7377 13.3627C10.6498 13.4506 10.5306 13.5 10.4062 13.5C10.2819 13.5 10.1627 13.4506 10.0748 13.3627C9.98689 13.2748 9.9375 13.1556 9.9375 13.0312V7.59375C9.9375 7.335 10.1475 7.125 10.4062 7.125Z" fill="currentColor"/></svg>' +
      '</button>' +
      '</div>' +
      (showVariant ? '<p class="oddit-cart-drawer__item-variant">' + escapeHtml(variantTitle) + '</p>' : '') +
      '</div>' +
      '<div class="oddit-cart-drawer__item-bottom">' +
      '<div class="oddit-cart-drawer__item-price-row">' +
      '<p class="oddit-cart-drawer__item-price">' +
      price +
      '</p>' +
      buildQtyOfferHTML(item) +
      '</div>' +
      '<div class="oddit-cart-drawer__item-actions">' +
      '<div class="oddit-cart-drawer__qty">' +
      '<button type="button" class="oddit-cart-drawer__qty-btn oddit-cart-drawer__qty-btn--left" data-cart-qty-decrease data-line-item-key="' +
      key +
      '" aria-label="Decrease quantity"><span>&ndash;</span></button>' +
      '<div class="oddit-cart-drawer__qty-value" data-cart-qty-value><span>' +
      item.quantity +
      '</span></div>' +
      '<button type="button" class="oddit-cart-drawer__qty-btn oddit-cart-drawer__qty-btn--right" data-cart-qty-increase data-line-item-key="' +
      key +
      '" aria-label="Increase quantity"><span>+</span></button>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '</li>'
    );
  }

  function buildQtyOfferHTML(item) {
    var root = getDrawer();
    if (!root || root.getAttribute('data-qty-offer-enabled') !== 'true') return '';
    var target = parseInt(root.getAttribute('data-qty-offer-quantity'), 10) || 0;
    var quantity = parseInt(item.quantity, 10) || 0;
    if (target < 2 || quantity >= target) return '';
    var remaining = target - quantity;
    var text = root.getAttribute('data-qty-offer-text') || '';
    var cta = (root.getAttribute('data-qty-offer-cta') || 'Add [remaining] More').replace('[remaining]', remaining);
    return (
      '<button type="button" class="oddit-cart-drawer__qty-offer" data-cart-qty-increase data-qty-step="' +
      remaining +
      '" data-line-item-key="' +
      item.key +
      '" aria-label="' +
      escapeHtml(text + '. ' + cta) +
      '">' +
      '<span class="oddit-cart-drawer__qty-offer-text">' + escapeHtml(text) + '</span>' +
      '<span class="oddit-cart-drawer__qty-offer-cta">' + escapeHtml(cta) + '</span>' +
      '</button>'
    );
  }

  function renderLineItems(root, cart) {
    var list = root.querySelector('.oddit-cart-drawer__items');
    if (!list) return;
    list.innerHTML = (cart.items || []).map(buildLineItemHTML).join('');
  }

  function updateTitleAndSubtotal(root, cart) {
    var titleEl = root.querySelector('.oddit-cart-drawer__title');
    var titleCountEl = root.querySelector('[data-cart-title-count]');
    if (titleCountEl) {
      titleCountEl.textContent = '(' + (cart.item_count || 0) + ')';
    } else if (titleEl) {
      if (!titleEl.dataset.titleBase) {
        titleEl.dataset.titleBase = titleEl.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      }
      titleEl.textContent = titleEl.dataset.titleBase + ' (' + (cart.item_count || 0) + ')';
    }
    var subtotalEl = root.querySelector('.oddit-cart-drawer__subtotal-value');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
  }

  function updateProgressBar(root, cart) {
    var thresholdDollars = parseFloat(root.getAttribute('data-free-shipping-threshold'));
    if (!thresholdDollars || thresholdDollars <= 0) return;

    var fills = root.querySelectorAll('.oddit-cart-drawer__progress-fill');
    var texts = root.querySelectorAll('.oddit-cart-drawer__shipping-text');

    var cartTotal = (cart.total_price || 0) / 100;
    var percentage = Math.min((cartTotal / thresholdDollars) * 100, 100);
    var remaining = Math.max(thresholdDollars - cartTotal, 0);
    var fillPx = Math.round(((percentage / 100) * PROGRESS_TRACK_PX) * 100) / 100;

    Array.prototype.forEach.call(fills, function (el) {
      el.style.width = fillPx + 'px';
    });
    Array.prototype.forEach.call(texts, function (el) {
      el.innerHTML =
        remaining > 0
          ? "You\u2019re <span class=\"oddit-cart-drawer__shipping-emph\">" + formatMoney(Math.round(remaining * 100)) +
            ' away</span> from <span class="oddit-cart-drawer__shipping-emph">FREE</span> Shipping'
          : 'You\u2019ve unlocked <span class="oddit-cart-drawer__shipping-emph">FREE</span> Shipping!';
    });
  }

  function applyCartStateVisibility(root, itemCount) {
    root.classList.toggle(EMPTY_CLASS, itemCount === 0);
    var filledEls = root.querySelectorAll('[data-cart-filled]');
    var emptyEls = root.querySelectorAll('[data-cart-empty]');
    Array.prototype.forEach.call(filledEls, function (el) {
      el.style.display = itemCount > 0 ? '' : 'none';
    });
    Array.prototype.forEach.call(emptyEls, function (el) {
      el.style.display = itemCount > 0 ? 'none' : '';
    });
  }

  function filterCrossSell(root, cart) {
    var cartProductIds = (cart.items || []).map(function (item) {
      return String(item.product_id);
    });
    var cards = root.querySelectorAll('.oddit-cart-drawer__cross-sell-card[data-product-id]');
    Array.prototype.forEach.call(cards, function (card) {
      var pid = String(card.getAttribute('data-product-id'));
      var inCart = cartProductIds.indexOf(pid) !== -1;
      card.style.display = inCart ? 'none' : '';
    });
  }

  function renderCrossSellRecommendations(root, cart) {
    var container = root.querySelector('.oddit-cart-drawer__cross-sell-products');
    if (!container) return;
    var source = container.getAttribute('data-cross-sell-source');
    if (source !== 'recommendations' && source !== 'metafield') return;

    var items = cart.items || [];
    if (!items.length) {
      container.innerHTML = '';
      return;
    }

    var referenceProductId = items[items.length - 1].product_id;
    var sectionRoot = root.closest('.shopify-section');
    var sectionId = sectionRoot ? sectionRoot.id.replace('shopify-section-', '') : root.id;
    var intent = container.getAttribute('data-cross-sell-intent') || 'complementary';
    var limit = parseInt(container.getAttribute('data-cross-sell-limit'), 10) || 4;

    var recommendationsUrl =
      '/recommendations/products' +
      '?section_id=' + encodeURIComponent(sectionId) +
      '&product_id=' + encodeURIComponent(referenceProductId) +
      '&limit=' + encodeURIComponent(limit) +
      '&intent=' + encodeURIComponent(intent);
    // Metafield mode is computed in Liquid from the cart, so re-render the section for the current page
    var url = source === 'metafield' ? window.location.pathname + '?section_id=' + encodeURIComponent(sectionId) : recommendationsUrl;

    function fetchCards(target) {
      return fetch(target)
        .then(function (res) {
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          return doc.querySelector('.oddit-cart-drawer__cross-sell-products');
        });
    }

    fetchCards(url)
      .then(function (fresh) {
        var hasCards = fresh && fresh.querySelector('.oddit-cart-drawer__cross-sell-card');
        if (!hasCards && source === 'metafield') return fetchCards(recommendationsUrl);
        return fresh;
      })
      .then(function (fresh) {
        if (fresh) {
          container.innerHTML = fresh.innerHTML;
          filterCrossSell(root, cart);
        }
      })
      .catch(function (err) {
        console.error('[oddit-cart-drawer] Cross-sell fetch failed:', err);
      });
  }

  function renderCart(cart) {
    if (!cart) return;
    cartState = cart;
    if (cart.currency) activeCurrency = cart.currency;

    var root = getDrawer();
    if (!root) {
      dispatchCartUpdated(cart);
      return;
    }

    renderLineItems(root, cart);
    updateTitleAndSubtotal(root, cart);
    updateProgressBar(root, cart);
    applyCartStateVisibility(root, cart.item_count || 0);
    filterCrossSell(root, cart);
    renderCrossSellRecommendations(root, cart);
    dispatchCartUpdated(cart);
  }

  /* -----------------------------------------------------------------
   * Event binding — line items, upsells, drawer chrome
   * --------------------------------------------------------------- */
  function bindLineItemEvents(root, signal) {
    var list = root.querySelector('.oddit-cart-drawer__items');
    if (!list) return;

    list.addEventListener(
      'click',
      function (e) {
        var decreaseBtn = e.target.closest('[data-cart-qty-decrease]');
        var increaseBtn = e.target.closest('[data-cart-qty-increase]');
        var removeBtn = e.target.closest('[data-cart-remove-item]');
        var actionBtn = decreaseBtn || increaseBtn || removeBtn;
        if (!actionBtn) return;

        // The buttons carry data-line-item-key too, so target the row element explicitly
        var lineItem = actionBtn.closest('.oddit-cart-drawer__item');
        if (lineItem && lineItem.classList.contains('is-busy')) return;

        var lineKey = actionBtn.getAttribute('data-line-item-key');
        if (!lineKey) return;

        if (lineItem) lineItem.classList.add('is-busy');

        var promise;
        if (removeBtn) {
          promise = removeFromCart(lineKey);
        } else {
          var valueEl = lineItem ? lineItem.querySelector('[data-cart-qty-value] span') : null;
          var current = valueEl ? parseInt(valueEl.textContent, 10) || 0 : 0;
          var step = increaseBtn ? parseInt(increaseBtn.getAttribute('data-qty-step'), 10) || 1 : 1;
          var next = Math.max(0, current + (increaseBtn ? step : -1));
          promise = updateLineItemQuantity(lineKey, next);
        }

        promise
          .catch(function (err) {
            console.error('[oddit-cart-drawer]', err);
          })
          .finally(function () {
            if (lineItem) lineItem.classList.remove('is-busy');
          });
      },
      { signal: signal }
    );
  }

  function bindUpsellEvents(root, signal) {
    root.addEventListener(
      'click',
      function (e) {
        // Never interfere with Shopify's accelerated-checkout web component.
        if (e.target.closest('[data-cart-additional-checkout]')) return;

        var checkoutBtn = e.target.closest('[data-cart-checkout]');
        if (checkoutBtn) {
          window.location.href = '/checkout';
          return;
        }

        var switchBtn = e.target.closest('.oddit-cart-drawer__switch-cta[data-selling-plan-id]');
        if (switchBtn) {
          e.preventDefault();
          var planId = switchBtn.getAttribute('data-selling-plan-id');
          var explicitKey = switchBtn.getAttribute('data-line-key') || switchBtn.getAttribute('data-line-item-key');
          var keys = [];
          if (explicitKey) {
            keys = [explicitKey];
          } else if (cartState && cartState.items) {
            keys = cartState.items
              .filter(function (item) {
                return !item.selling_plan_allocation;
              })
              .map(function (item) {
                return item.key;
              });
          }
          if (!keys.length || !planId) return;

          switchBtn.disabled = true;
          var chain = Promise.resolve();
          keys.forEach(function (key) {
            chain = chain.then(function () {
              return updateLineItemToSubscription(key, planId);
            });
          });
          chain
            .catch(function (err) {
              console.error('[oddit-cart-drawer]', err);
            })
            .finally(function () {
              switchBtn.disabled = false;
            });
          return;
        }

        var addBtn = e.target.closest(
          '[data-add-to-cart][data-variant-id], ' +
            '.oddit-cart-drawer__upsell-cta[data-variant-id], ' +
            '.oddit-cart-drawer__rec-add[data-variant-id], ' +
            '.oddit-cart-drawer__cross-sell-add[data-variant-id]'
        );
        if (!addBtn) return;

        var variantId = Number(addBtn.getAttribute('data-variant-id'));
        if (!variantId) return;

        e.preventDefault();

        var restWidth = addBtn.getBoundingClientRect().width;
        addBtn.style.minWidth = restWidth + 'px';
        addBtn.disabled = true;
        addBtn.classList.add('is-loading');
        addBtn.setAttribute('aria-busy', 'true');

        var quantity = Number(addBtn.getAttribute('data-quantity')) || 1;
        addToCart(variantId, quantity)
          .then(function () {
            openDrawer();
          })
          .catch(function (err) {
            console.error('[oddit-cart-drawer]', err);
          })
          .finally(function () {
            addBtn.disabled = false;
            addBtn.classList.remove('is-loading');
            addBtn.removeAttribute('aria-busy');
            addBtn.style.minWidth = '';
          });
      },
      { signal: signal }
    );
  }

  // Cross-sell pills: delegated on the drawer root so re-fetched cards keep working
  function bindCrossSellOptions(root, signal) {
    root.addEventListener(
      'change',
      function (e) {
        var input = e.target.closest('.oddit-cart-drawer__cross-sell-size-input');
        if (!input) return;
        var card = input.closest('[data-cross-sell-card]');
        var data = card && card.querySelector('[data-cs-variants]');
        var addBtn = card && card.querySelector('.oddit-cart-drawer__cross-sell-add');
        if (!data || !addBtn) return;

        var variants = JSON.parse(data.textContent);
        var index = Number(input.getAttribute('data-cs-option-index'));
        var current = variants.find(function (v) { return String(v.id) === addBtn.getAttribute('data-variant-id'); }) || variants[0];
        var wanted = current.options.slice();
        wanted[index] = input.value;
        // Exact match on the other options first, else any available variant with this value
        var variant =
          variants.find(function (v) { return v.options.every(function (o, i) { return o === wanted[i]; }); }) ||
          variants.find(function (v) { return v.options[index] === input.value && v.available; });

        card.querySelectorAll('.oddit-cart-drawer__cross-sell-size').forEach(function (pill) {
          pill.classList.toggle('oddit-cart-drawer__cross-sell-size--selected', pill.contains(input));
        });
        if (!variant) return;
        addBtn.setAttribute('data-variant-id', variant.id);
        addBtn.disabled = !variant.available;
        var price = card.querySelector('[data-cs-price]');
        if (price) price.textContent = formatMoney(variant.price);
      },
      { signal: signal }
    );
  }

  function bindDrawerChrome(root, signal) {
    root.addEventListener(
      'click',
      function (e) {
        if (e.target.closest('[data-cart-drawer-close]')) {
          closeDrawer();
        }
      },
      { signal: signal }
    );

    document.addEventListener(
      'keydown',
      function (e) {
        if (!root.classList.contains(OPEN_CLASS)) return;
        if (e.key === 'Escape') {
          closeDrawer();
          return;
        }
        if (e.key === 'Tab') trapFocus(e, root);
      },
      { signal: signal }
    );
  }

  /* -----------------------------------------------------------------
   * Lifecycle — init / destroy, theme-editor section reload safe
   * --------------------------------------------------------------- */
  function initDrawer() {
    var root = getDrawer();
    if (!root) return;

    if (abortController) abortController.abort();
    abortController = new AbortController();
    var signal = abortController.signal;

    bindDrawerChrome(root, signal);
    bindLineItemEvents(root, signal);
    bindUpsellEvents(root, signal);
    bindCrossSellOptions(root, signal);

    // The CSS/Liquid mismatch this guards against: the Liquid root only
    // marks the server-rendered empty/filled state via the is-empty class
    // on initial load. Apply the same on init so [data-cart-filled] /
    // [data-cart-empty] visibility is correct before any AJAX round trip.
    applyCartStateVisibility(root, root.classList.contains(EMPTY_CLASS) ? 0 : 1);
  }

  function destroyDrawer() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  /* Any trigger elsewhere on the page with data-cart-drawer-open / data-cart-drawer-toggle */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-cart-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer();
      return;
    }
    var toggler = e.target.closest('[data-cart-drawer-toggle]');
    if (toggler) {
      e.preventDefault();
      toggleDrawer();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDrawer);
  } else {
    initDrawer();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector(DRAWER_SELECTOR)) {
      initDrawer();
      refreshCart().catch(function () {
        /* noop */
      });
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target.querySelector(DRAWER_SELECTOR)) {
      destroyDrawer();
    }
  });

  /* -----------------------------------------------------------------
   * window.OdditCartDrawer public API (cart-drawer-js-api.mdc,
   * add-to-cart-integration.mdc). Every other section on the site
   * (header cart icon, PDP buy buttons, collection quick-add) calls
   * through this object instead of hitting the Cart AJAX API directly.
   * --------------------------------------------------------------- */
  window.OdditCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    toggle: toggleDrawer,
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart,
    renderCart: renderCart,
    formatMoney: formatMoney
  };

  /* -----------------------------------------------------------------
   * Theme editor: preserve open state across section reloads
   * (cart-drawer-theme-editor-state.mdc)
   * --------------------------------------------------------------- */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    var STATE_KEY = 'oddit-cart-drawer-editor-open';

    function findDrawerIn(node) {
      if (!node || typeof node.querySelector !== 'function') return null;
      if (node.matches && node.matches(DRAWER_SELECTOR)) return node;
      return node.querySelector(DRAWER_SELECTOR);
    }

    document.addEventListener('shopify:section:unload', function (event) {
      var drawer = findDrawerIn(event.target);
      if (!drawer) return;
      try {
        sessionStorage.setItem(STATE_KEY, drawer.classList.contains(OPEN_CLASS) ? '1' : '0');
      } catch (e) {
        /* sessionStorage may be blocked — ignore */
      }
    });

    document.addEventListener('shopify:section:load', function (event) {
      var drawer = findDrawerIn(event.target);
      if (!drawer) return;
      var wasOpen;
      try {
        wasOpen = sessionStorage.getItem(STATE_KEY) === '1';
      } catch (e) {
        wasOpen = false;
      }
      if (!wasOpen) return;
      requestAnimationFrame(function () {
        if (window.OdditCartDrawer && typeof window.OdditCartDrawer.open === 'function') {
          window.OdditCartDrawer.open();
        }
      });
    });

    document.addEventListener('shopify:section:select', function (event) {
      var drawer = findDrawerIn(event.target);
      if (!drawer) return;
      if (window.OdditCartDrawer) window.OdditCartDrawer.open();
    });

    document.addEventListener('shopify:block:select', function (event) {
      var sectionEl = event.target && event.target.closest && event.target.closest('.shopify-section');
      var drawer = findDrawerIn(sectionEl) || findDrawerIn(event.target);
      if (!drawer) return;
      if (window.OdditCartDrawer) window.OdditCartDrawer.open();
    });
  }
})();
